import { useEffect, useState, type RefObject } from 'react';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Dialog, MenuItem, MenuList, Popover, Select, Textarea } from '../../ds/components';
import { errorMessage } from '../../lib/api';
import { useTask, type ReassignMode, type TaskItem, type UserRef } from './api';
import styles from './StuckActions.module.css';

export type StuckAction =
  { mode: 'take_over' } | { mode: 'give_back' } | { mode: 'assign' } | { mode: 'escalate' };

export interface StuckMenuProps {
  open: boolean;
  task: TaskItem;
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  onSelect: (mode: ReassignMode) => void;
}

/**
 * Backoffice 02 „Mehr": Übernehmen · Zurückgeben an <Name> · Neu zuweisen · An <Name> eskalieren, and the
 * note of the last skip underneath. Give-back and escalation need the previous assignee / a manager,
 * which the row's `stuck` info names; the entries are left out otherwise.
 */
export function StuckMenu({ open, task, anchorRef, onClose, onSelect }: StuckMenuProps) {
  const detail = useTask(open ? task.id : null);
  const skips = detail.data?.skips ?? [];
  const noteIndex = skips.findLastIndex((entry) => entry.note !== null && entry.note !== '');
  const lastSkip = noteIndex >= 0 ? skips[noteIndex] : undefined;
  const stuck = task.stuck;
  const pick = (mode: ReassignMode) => {
    onClose();
    onSelect(mode);
  };
  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      placement="bottom-end"
      offset={6}
      width={280}
      variant="none"
      className={styles.menu}
      initialFocus="none"
    >
      <MenuList label="Aktionen" onClose={onClose}>
        <MenuItem
          className={styles.item}
          label="Übernehmen"
          description="Task wechselt zu Ihnen"
          onSelect={() => pick('take_over')}
        />
        {stuck?.previousAssignee ? (
          <MenuItem
            className={styles.item}
            active={stuck.reason === 'skipped'}
            label={`Zurückgeben an ${stuck.previousAssignee.name}`}
            description="mit Notiz, Zähler wird zurückgesetzt"
            onSelect={() => pick('give_back')}
          />
        ) : null}
        <MenuItem
          className={styles.item}
          label="Neu zuweisen"
          description="an eine andere Person"
          onSelect={() => pick('assign')}
        />
        {stuck?.escalation ? (
          <MenuItem
            className={styles.item}
            label={`An ${stuck.escalation.user.name} eskalieren`}
            description={stuck.escalation.label}
            onSelect={() => pick('escalate')}
          />
        ) : null}
      </MenuList>
      {lastSkip ? (
        <div className={styles.note}>
          „{lastSkip.note}“
          <div className={styles.noteMeta}>
            {lastSkip.by?.name ?? 'Unbekannt'} · Notiz beim {noteIndex + 1}. Überspringen
          </div>
        </div>
      ) : null}
    </Popover>
  );
}

export interface ReassignDialogProps {
  open: boolean;
  mode: Exclude<ReassignMode, 'take_over'>;
  task: TaskItem;
  assignees: ReadonlyArray<UserRef>;
  pending: boolean;
  error: unknown;
  fieldErrors: Record<string, string>;
  onClose: () => void;
  onSubmit: (input: { assigneeId?: string; reason: string | null }) => void;
}

const TITLES: Record<Exclude<ReassignMode, 'take_over'>, { title: string; submit: string }> = {
  give_back: { title: 'Task zurückgeben', submit: 'Zurückgeben' },
  assign: { title: 'Task neu zuweisen', submit: 'Zuweisen' },
  escalate: { title: 'Task eskalieren', submit: 'Eskalieren' },
};

/** Note/assignee dialog of the three reassign modes that need input (Backoffice 02). */
export function ReassignDialog({
  open,
  mode,
  task,
  assignees,
  pending,
  error,
  fieldErrors,
  onClose,
  onSubmit,
}: ReassignDialogProps) {
  const [assigneeId, setAssigneeId] = useState('');
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    if (!open) return;
    setAssigneeId('');
    setReason('');
    setMissing(false);
  }, [open]);

  const texts = TITLES[mode];
  const target =
    mode === 'give_back'
      ? task.stuck?.previousAssignee?.name
      : mode === 'escalate'
        ? task.stuck?.escalation?.user.name
        : undefined;
  const options = assignees
    .filter((user) => user.id !== task.assignee.id)
    .map((user) => ({ value: user.id, label: user.name }));

  function submit() {
    if (mode === 'assign' && assigneeId === '') {
      setMissing(true);
      return;
    }
    onSubmit({
      ...(mode === 'assign' ? { assigneeId } : {}),
      reason: reason.trim() === '' ? null : reason.trim(),
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      eyebrow="Hängt fest"
      title={texts.title}
      subtitle={target ? `${texts.submit} an ${target} · ${task.title}` : task.title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button onClick={submit} loading={pending}>
            {texts.submit}
          </Button>
        </>
      }
    >
      <div className={styles.dialogBody}>
        {error ? <Banner tone="danger">{errorMessage(error)}</Banner> : null}
        {mode === 'assign' ? (
          options.length > 0 ? (
            <Select
              label="Neue zuständige Person"
              required
              placeholder="Bitte wählen"
              options={options}
              value={assigneeId}
              error={fieldErrors['assigneeId'] ?? (missing ? 'Bitte eine Person wählen.' : undefined)}
              onChange={(event) => {
                setAssigneeId(event.target.value);
                setMissing(false);
              }}
            />
          ) : (
            <Banner tone="neutral">Es gibt keine weitere Person, der Sie diese Task zuweisen können.</Banner>
          )
        ) : null}
        <Textarea
          label={mode === 'give_back' ? 'Notiz' : 'Grund'}
          optional
          rows={3}
          value={reason}
          error={fieldErrors['reason']}
          onChange={(event) => setReason(event.target.value)}
        />
        {mode === 'give_back' ? (
          <div className={clsx(styles.hint)}>Der Zähler für „Übersprungen“ wird zurückgesetzt.</div>
        ) : null}
      </div>
    </Dialog>
  );
}
